import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useCategories } from '../../../hooks/useCategories';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, Button } from '../../../components/ui';
import { Upload, FileText, Trash2, X, CheckCircle, Ticket, Plus, AlertCircle, Loader2, Phone, GitBranch } from 'lucide-react';
import { ticketService } from '../../../api/ticketService';
import { fileService } from '../../../api/fileService';
import { getTemplateByCategory } from '../../../api/templateService';
import LocationSection from '../../../components/location/LocationSection';
import { useTicketRelationsEnabled } from '../../../contexts/AppConfigContext';
import { apiGet } from '../../../services/apiClient';
import { KENYA_COUNTRY_ID } from '../../../constants/kenyaLocations';
import toast from 'react-hot-toast';

/** Read region/county/subCounty from session userDetails when already loaded (e.g. profile). */
function readCachedCustomerLocation() {
  try {
    const raw = sessionStorage.getItem('userDetails');
    if (!raw) return null;
    const details = JSON.parse(raw);
    const regionId = details?.regionId ?? details?.locationInfo?.regionId;
    const countyId = details?.countyId ?? details?.locationInfo?.countyId;
    const subCountyId = details?.subCountyId ?? details?.locationInfo?.subCountyId;
    if (regionId == null || countyId == null || subCountyId == null) return null;
    return { regionId, countyId, subCountyId };
  } catch {
    return null;
  }
}

function writeCachedCustomerLocation({ regionId, countyId, subCountyId }) {
  try {
    const raw = sessionStorage.getItem('userDetails');
    const details = raw ? JSON.parse(raw) : {};
    details.regionId = Number(regionId);
    details.countyId = Number(countyId);
    details.subCountyId = Number(subCountyId);
    sessionStorage.setItem('userDetails', JSON.stringify(details));
  } catch {
    // ignore cache write failures
  }
}

const CreateTicketForm = ({
  source = 'portal',
  redirectPath = '/portal/tickets',
  title = 'Create New Ticket',
  subtitle = 'Submit a support request to get help with your issue',
  phoneNumber: initialPhoneNumber = null
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // V1 parent-child "create child" path. Strict gating to keep customer portal
  // behavior 100% unchanged:
  //   1. URL must carry ?parentTicketId=NNN AND
  //   2. caller must be agent (`source === 'agent'`) AND
  //   3. backend feature flag `ticketRelations` must be on.
  // If any gate fails, the form behaves exactly as before — no banner, regular create API.
  const ticketRelationsEnabled = useTicketRelationsEnabled();
  const rawParentTicketId = searchParams.get('parentTicketId');
  const rawParentTicketNumber = searchParams.get('parentTicketNumber');
  const parentTicketIdNum = (() => {
    const n = Number(rawParentTicketId);
    return Number.isFinite(n) && n > 0 ? n : null;
  })();
  const isCreateChildMode = ticketRelationsEnabled
    && source === 'agent'
    && parentTicketIdNum !== null;

  const [formData, setFormData] = useState({
    subject: '',
    categoryId: null,
    description: '',
    phoneNumber: '',
    attachments: []
  });
  const [locationData, setLocationData] = useState({
    countryId: null,
    regionId: null,
    countyId: null,
    subCountyId: null,
    wardId: null
  });
  const [locationInitialValues, setLocationInitialValues] = useState({});
  const [locationPrefillKey, setLocationPrefillKey] = useState('manual');
  const { data: categoriesRaw = [], isLoading: loadingCategories, isSuccess: categoriesReady, isError: categoriesError } = useCategories();
  const loadingReferences = loadingCategories;

  const categories = useMemo(() => {
    const activeCategories = categoriesRaw.filter(
      (cat) =>
        cat.categoryId !== null &&
        (cat.isActive === true || cat.isActive === undefined || cat.isActive === 1)
    );
    return [...activeCategories].sort((a, b) => {
      if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
        return (a.displayOrder || 0) - (b.displayOrder || 0);
      }
      return (a.categoryName || '').localeCompare(b.categoryName || '');
    });
  }, [categoriesRaw]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [createdTicketId, setCreatedTicketId] = useState(null);
  const [oversizedFiles, setOversizedFiles] = useState([]); // Track files that exceed size limit
  const fileInputRef = React.useRef(null);
  const submitInProgressRef = React.useRef(false);
  /** Section roots for scroll-to-first validation error on submit */
  const fieldScrollRefs = useRef({});

  // Template fields state
  const [templateFields, setTemplateFields] = useState([]);
  const [loadingTemplateFields, setLoadingTemplateFields] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState({}); // Store custom field values

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB per file

  // Prefill location from the logged-in customer's saved user_locations (portal only).
  // Prefer session cache (profile already loaded); else same real API as UnifiedProfile
  // — not getUserById, which serves mock users without location in development.
  useEffect(() => {
    if (source !== 'portal') {
      return;
    }
    const applyPrefill = (regionId, countyId, subCountyId) => {
      setLocationInitialValues({ regionId, countyId, subCountyId });
      setLocationData({
        countryId: KENYA_COUNTRY_ID,
        regionId: Number(regionId),
        countyId: Number(countyId),
        subCountyId: Number(subCountyId),
        wardId: null,
      });
      setLocationPrefillKey(`prefill-${regionId}-${countyId}-${subCountyId}`);
      writeCachedCustomerLocation({ regionId, countyId, subCountyId });
    };

    const cached = readCachedCustomerLocation();
    if (cached) {
      applyPrefill(cached.regionId, cached.countyId, cached.subCountyId);
      return;
    }

    const userId = sessionStorage.getItem('userId');
    if (!userId) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await apiGet(
          `/gateway/registration/api/signup/organization/users/${userId}`
        );
        const user = response?.data;
        if (cancelled || !user) {
          return;
        }
        const regionId = user.regionId ?? null;
        const countyId = user.countyId ?? null;
        const subCountyId = user.subCountyId ?? null;
        if (regionId == null || countyId == null || subCountyId == null) {
          return;
        }
        applyPrefill(regionId, countyId, subCountyId);
      } catch (error) {
        console.warn('Could not prefill customer location:', error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [source]);

  // Load phone number from props, location state, or query params on mount.
  // For portal users only, fallback to sessionStorage user details.
  useEffect(() => {
    // Priority: explicit prop -> location state -> query params
    const phoneFromProp = initialPhoneNumber;
    const phoneFromState = location.state?.phoneNumber;
    const phoneFromQuery = searchParams.get('phone_number') || searchParams.get('phone');

    if (phoneFromProp || phoneFromState || phoneFromQuery) {
      const phoneNumber = phoneFromProp || phoneFromState || decodeURIComponent(phoneFromQuery || '');
      // Only keep numeric characters and limit to 10 digits
      const numericPhone = phoneNumber.replace(/\D/g, '').slice(0, 10);
      setFormData(prev => ({
        ...prev,
        phoneNumber: numericPhone
      }));
      return;
    }

    if (source === 'agent') {
      return;
    }

    // Otherwise, get from sessionStorage userDetails
    try {
      const userDetailsStr = sessionStorage.getItem('userDetails');
      if (userDetailsStr) {
        const userDetails = JSON.parse(userDetailsStr);
        if (userDetails?.phone) {
          // Only keep numeric characters and limit to 10 digits
          const numericPhone = userDetails.phone.replace(/\D/g, '').slice(0, 10);
          setFormData(prev => ({
            ...prev,
            phoneNumber: numericPhone
          }));
        }
      }
    } catch (error) {
      console.error('Error parsing userDetails from sessionStorage:', error);
      // Silently fail - phone number is optional
    }
  }, [initialPhoneNumber, location.state, searchParams, source]);

  const referenceToastShownRef = useRef(false);
  useEffect(() => {
    if (loadingReferences || referenceToastShownRef.current) {
      return;
    }
    if (!categoriesReady) {
      return;
    }
    referenceToastShownRef.current = true;
    if (categoriesError) {
      toast.error('Failed to load ticket categories');
      return;
    }
    if (categories.length === 0) {
      toast.error('No active ticket categories available. Please contact support.');
    }
  }, [
    loadingReferences,
    categoriesError,
    categoriesReady,
    categories.length,
  ]);

  const computeValidationErrors = () => {
    const newErrors = {};

    if (!formData.subject || formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    if (!formData.description || formData.description.trim().length < 20) {
      newErrors.description = 'Description must be at least 20 characters';
    }

    if (!formData.categoryId) {
      newErrors.category = 'Please select a category';
    }

    if (!locationData.regionId) {
      newErrors.regionId = 'Region is required';
    }
    if (!locationData.countyId) {
      newErrors.countyId = 'County is required';
    }

    // Validate custom fields
    templateFields.forEach(field => {
      if (field.isRequired) {
        const value = customFieldValues[field.fieldCode];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          newErrors[`custom_${field.fieldCode}`] = `${field.fieldName} is required`;
        }
      }
    });

    // Validate file sizes
    const hasOversizedFiles = oversizedFiles.length > 0 ||
      formData.attachments.some(file => file.size > MAX_FILE_SIZE);

    if (hasOversizedFiles) {
      newErrors.attachments = 'One or more files exceed the maximum size of 5MB. Please remove oversized files before submitting.';
    }

    return newErrors;
  };

  /** First error key in top-to-bottom form order (matches scroll targets). */
  const getFirstErrorScrollKey = (newErrors) => {
    const orderedKeys = [
      'subject',
      'category',
      'regionId',
      'countyId',
      'description',
      ...templateFields.map((f) => `custom_${f.fieldCode}`),
      'attachments',
    ];
    for (const k of orderedKeys) {
      if (newErrors[k]) return k;
    }
    return Object.keys(newErrors)[0] ?? null;
  };

  const scrollToFirstErrorField = (key) => {
    if (!key) return;
    const el = fieldScrollRefs.current[key];
    if (!el || typeof el.scrollIntoView !== 'function') return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const focusable = el.querySelector('input:not([type="hidden"]):not([type="file"]), select, textarea');
    if (focusable && typeof focusable.focus === 'function') {
      window.setTimeout(() => {
        try {
          focusable.focus({ preventScroll: true });
        } catch {
          focusable.focus();
        }
      }, 400);
    }

  };

  // Fetch template fields when category changes (only if category uses custom fields)
  useEffect(() => {
    const fetchTemplateFields = async () => {
      if (!formData.categoryId) {
        setTemplateFields([]);
        setCustomFieldValues({});
        return;
      }

      // First, check if the selected category uses custom fields (templateType = "2")
      const selectedCategory = categories.find(cat =>
        (cat.categoryId || cat.id) === formData.categoryId
      );

      // Only fetch template fields if category uses custom fields (templateType = "2")
      if (!selectedCategory || (selectedCategory.templateType !== '2' && selectedCategory.template_type !== '2')) {
        setTemplateFields([]);
        setCustomFieldValues({});
        return;
      }

      setLoadingTemplateFields(true);
      try {
        const response = await getTemplateByCategory(formData.categoryId);
        const templateData = response?.data?.data || response?.data;

        if (templateData?.fields && Array.isArray(templateData.fields)) {
          // Sort by displayOrder; ticket form only shows active fields
          const sortedFields = [...templateData.fields]
            .filter((f) => f.isActive !== false)
            .sort((a, b) =>
            (a.displayOrder || 0) - (b.displayOrder || 0)
          );
          setTemplateFields(sortedFields);

          // Initialize custom field values
          const initialValues = {};
          sortedFields.forEach(field => {
            initialValues[field.fieldCode] = '';
          });
          setCustomFieldValues(initialValues);
        } else {
          setTemplateFields([]);
          setCustomFieldValues({});
        }
      } catch (error) {
        console.error('Error fetching template fields:', error);
        // Don't show error toast - category might not have template fields
        setTemplateFields([]);
        setCustomFieldValues({});
      } finally {
        setLoadingTemplateFields(false);
      }
    };

    fetchTemplateFields();
  }, [formData.categoryId, categories]);

  const handleInputChange = (field, value) => {
    // For phone number, only allow numeric input and limit to 10 digits
    if (field === 'phoneNumber') {
      // Remove all non-numeric characters
      const numericValue = value.replace(/\D/g, '');
      // Limit to 10 digits
      const limitedValue = numericValue.slice(0, 10);
      setFormData(prev => ({
        ...prev,
        [field]: limitedValue
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleCustomFieldChange = (fieldCode, value) => {
    setCustomFieldValues(prev => ({
      ...prev,
      [fieldCode]: value
    }));

    // Clear error when user starts typing
    if (errors[`custom_${fieldCode}`]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`custom_${fieldCode}`];
        return newErrors;
      });
    }
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    const newOversizedFiles = []; // 5MB limit
    const validFiles = [];

    files.forEach(file => {
      if (file.size > MAX_FILE_SIZE) {
        newOversizedFiles.push({
          name: file.name,
          size: file.size
        });
        toast.error(`File "${file.name}" exceeds the maximum size of 5MB. Please select a smaller file.`);
      } else {
        validFiles.push(file);
      }
    });

    // Update oversized files list
    setOversizedFiles(prev => [...prev, ...newOversizedFiles]);

    // Add only valid files to attachments
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));

    // Clear error for attachments if all files are valid
    if (newOversizedFiles.length === 0 && validFiles.length > 0) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
    // Clear attachment errors if no attachments remain
    if (formData.attachments.length === 1) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
      setOversizedFiles([]);
    }
  };

  const removeOversizedFile = (index) => {
    setOversizedFiles(prev => prev.filter((_, i) => i !== index));
    // Clear attachment errors if no oversized files remain
    if (oversizedFiles.length === 1) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.attachments;
        return newErrors;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const sanitizeTicketCreationError = (rawMessage) => {
    const fallback = 'Failed to create ticket. Please try again.';
    if (!rawMessage) return fallback;

    const message = String(rawMessage).replace(/^API Error:\s*\d+\s*/i, '').trim();
    if (!message) return fallback;

    const technicalPattern =
      /could not execute statement|duplicate key value|violates|constraint|sqlstate|org\.hibernate|jdbc|insert into|detail:\s*key|position:\s*\d+/i;

    // Hide backend SQL/ORM internals from end users.
    if (technicalPattern.test(message) || message.length > 220) {
      return fallback;
    }

    return message;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submitInProgressRef.current) {
      return;
    }
    submitInProgressRef.current = true;

    const newErrors = computeValidationErrors();
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      const firstKey = getFirstErrorScrollKey(newErrors);
      window.setTimeout(() => scrollToFirstErrorField(firstKey), 0);
      submitInProgressRef.current = false;
      return;
    }

    setSubmitting(true);
    try {
      // Validate form data before sending
      if (!formData.subject || !formData.description || !formData.categoryId) {
        toast.error('Please fill in all required fields');
        setSubmitting(false);
        submitInProgressRef.current = false;
        return;
      }

      // Transform data to match backend DTO format (priority is set by backend to Not Prioritised)
      const ticketData = {
        subject: formData.subject.trim(),
        description: formData.description.trim(),
        categoryId: Number(formData.categoryId), // Ensure it's a number
        source: source, // Use prop value (defaults to 'portal' for customer portal, 'agent' for agent portal)
        channel: 'web',
        // Include client timestamp (desktop time) in ISO 8601 format with timezone
        // This preserves the desktop time as the user sees it (e.g., 06:16 in Kenya +05:30)
        clientCreatedAt: (() => {
          const now = new Date();
          // Get timezone offset in minutes and convert to ±HH:MM format
          const offset = -now.getTimezoneOffset();
          const offsetHours = Math.floor(Math.abs(offset) / 60);
          const offsetMinutes = Math.abs(offset) % 60;
          const offsetSign = offset >= 0 ? '+' : '-';
          const offsetStr = `${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;

          // Format as ISO 8601 with timezone (e.g., "2026-02-23T06:16:00+05:30")
          const year = now.getFullYear();
          const month = String(now.getMonth() + 1).padStart(2, '0');
          const day = String(now.getDate()).padStart(2, '0');
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');
          const milliseconds = String(now.getMilliseconds()).padStart(3, '0');

          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}${offsetStr}`;
        })()
      };

      // Add phone number if provided
      if (formData.phoneNumber && formData.phoneNumber.trim()) {
        ticketData.phoneNumber = formData.phoneNumber.trim();
      }

      // Add location data if provided
      if (locationData.countryId) {
        ticketData.countryId = locationData.countryId;
      }
      if (locationData.regionId) {
        ticketData.regionId = locationData.regionId;
      }
      if (locationData.countyId) {
        ticketData.countyId = locationData.countyId;
      }
      if (locationData.subCountyId) {
        ticketData.subCountyId = locationData.subCountyId;
      }
      if (locationData.wardId) {
        ticketData.wardId = locationData.wardId;
      }

      // Include dynamic custom field values for template-based categories.
      if (templateFields.length > 0) {
        const normalizedCustomFieldValues = Object.entries(customFieldValues || {}).reduce((acc, [fieldCode, rawValue]) => {
          if (rawValue === null || rawValue === undefined) {
            return acc;
          }
          if (Array.isArray(rawValue)) {
            const cleaned = rawValue
              .map((item) => (item === null || item === undefined ? '' : String(item).trim()))
              .filter((item) => item.length > 0);
            if (cleaned.length > 0) {
              acc[fieldCode] = cleaned;
            }
            return acc;
          }
          if (typeof rawValue === 'string') {
            const trimmed = rawValue.trim();
            if (trimmed.length > 0) {
              acc[fieldCode] = trimmed;
            }
            return acc;
          }
          acc[fieldCode] = rawValue;
          return acc;
        }, {});

        ticketData.customFieldValues = normalizedCustomFieldValues;
      }

      // Log the data being sent for debugging
      //console.log('Sending ticket data:', ticketData);

      // Create ticket — branches on parent-child create-child mode (V1, agent only).
      // The createChildTicket endpoint internally orchestrates createTicket + relation
      // insert in one transaction, so attachments and post-create flow stay identical.
      let response;
      if (isCreateChildMode) {
        response = await ticketService.createChildTicket(parentTicketIdNum, ticketData);
        // The create-child endpoint returns { child: TicketResponse, relation: RelationResponse }
        // wrapped in ResponseBean. Normalize the shape so the rest of this handler can
        // continue treating `response.data.data` as a TicketResponse.
        const apiData = response?.data;
        if (apiData?.success && apiData?.data?.child) {
          response = { data: { ...apiData, data: apiData.data.child } };
        }
      } else {
        response = await ticketService.createTicket(ticketData);
      }

      // Backend returns ResponseBean: { status, message, data, success, timestamp }
      const responseData = response?.data;

      if (responseData?.success && responseData?.data) {
        const createdTicket = responseData.data;
        const ticketId = createdTicket.ticketId;

        // Handle attachments if any - Use file service API
        // If uploads fail, don't show success - ticket will be rolled back by backend
        if (formData.attachments.length > 0 && ticketId) {
          try {
            // Use Promise.all - fails if any upload fails
            await Promise.all(
              formData.attachments.map(async (file) => {
                try {
                  await fileService.uploadFile(file, {
                    reg_id: parseInt(ticketId),
                    fileTypeId: 1,
                    isActive: true,
                    screenName: 'TICKET'
                  });
                } catch (fileUploadError) {
                  // Attach file name to error for better reporting
                  fileUploadError.fileName = file.name;
                  throw fileUploadError; // Re-throw to be caught by outer Promise.all
                }
              })
            );

            // All uploads successful
            toast.success(`${formData.attachments.length} file(s) uploaded successfully`);
          } catch (uploadError) {
            console.error('Error uploading files:', uploadError);

            // Extract error message from API response (ResponseModel structure)
            let errorMessage = 'File upload failed. Please check file size and type.';

            // Helper function to extract clean message from error
            const extractApiMessage = (error) => {
              // Priority 1: Check if error has response.data (axios-style structure)
              if (error?.response?.data) {
                const data = error.response.data;
                // Try ResponseModel structure: { response: { response: "message" } }
                if (data.response?.response) {
                  return data.response.response;
                }
                // Try direct response field
                if (data.response && typeof data.response === 'string') {
                  return data.response;
                }
                // Try message field
                if (data.message) {
                  return data.message;
                }
                // Try error field
                if (data.error) {
                  return data.error;
                }
                // Try nested data.message
                if (data.data?.message) {
                  return data.data.message;
                }
                // Try nested data.error
                if (data.data?.error) {
                  return data.data.error;
                }
              }

              // Priority 2: Check error.message (apiClient throws Error with message)
              if (error?.message) {
                let msg = error.message;

                // Remove "API Error: XXX" prefix if present
                if (msg.startsWith('API Error:')) {
                  // Try to extract actual message after "API Error: XXX"
                  const parts = msg.split('API Error:');
                  if (parts.length > 1) {
                    const afterPrefix = parts.slice(1).join('API Error:').trim();
                    // If there's meaningful content after the prefix, use it
                    if (afterPrefix && afterPrefix.length > 0 && !afterPrefix.match(/^\d+$/)) {
                      return afterPrefix;
                    }
                  }
                  // If no meaningful message after prefix, return generic message
                  return 'File size exceeds maximum allowed limit. Please select a smaller file.';
                }

                // Check if message is a JSON string that can be parsed
                if (typeof msg === 'string' && (msg.trim().startsWith('{') || msg.trim().startsWith('['))) {
                  try {
                    const parsed = JSON.parse(msg);
                    return parsed.message || parsed.error || parsed.response?.response || msg;
                  } catch {
                    // Not valid JSON, continue
                  }
                }

                return msg;
              }

              return null;
            };

            const extractedMessage = extractApiMessage(uploadError);

            if (extractedMessage) {
              // Remove "API Error: XXX" prefix if it still exists
              errorMessage = extractedMessage.replace(/^API Error:\s*\d+\s*/i, '').trim();

              // If errorMessage is just a number (status code), use default
              if (errorMessage.match(/^\d+$/)) {
                errorMessage = 'File size exceeds maximum allowed limit. Please select a smaller file.';
              }

              // If we have a file name, include it
              if (uploadError?.fileName) {
                errorMessage = `${uploadError.fileName}: ${errorMessage}`;
              }
            } else {
              // Fallback to default message
              if (uploadError?.fileName) {
                errorMessage = `${uploadError.fileName}: ${errorMessage}`;
              }
            }

            // Show error - ticket creation failed due to file upload failure
            // Backend will rollback the ticket automatically
            toast.error(`Ticket creation failed: ${errorMessage}`, { duration: 8000 });
            setSubmitting(false);
            submitInProgressRef.current = false;

            // Don't show success screen - ticket was rolled back
            return; // Exit early - don't show success
          }
        }

        // Only set success if we reach here (no attachments or all uploads succeeded)
        setCreatedTicketId(ticketId);
        setShowSuccess(true);

        // Reset form for next submission
        setFormData({
          subject: '',
          categoryId: null, // No default - user must select
          description: '',
          phoneNumber: '', // Reset phone number
          attachments: []
        });
        setLocationData({
          countryId: null,
          regionId: null,
          countyId: null,
          subCountyId: null,
          wardId: null
        });
        setLocationInitialValues({});
        setLocationPrefillKey(`reset-${Date.now()}`);
        setErrors({});
        setOversizedFiles([]); // Clear oversized files list
        setTemplateFields([]);
        setCustomFieldValues({});
      } else {
        toast.error(sanitizeTicketCreationError(responseData?.message));
      }
    } catch (error) {
      console.error('Submit error:', error);
      const rawErrorMessage = error?.response?.data?.message ||
        error?.message ||
        'An error occurred. Please try again.';
      toast.error(sanitizeTicketCreationError(rawErrorMessage));
    } finally {
      setSubmitting(false);
      submitInProgressRef.current = false;
    }
  };

  const handleCancel = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate(redirectPath);
    }
  };

  const handleGoToMyTickets = () => {
    navigate(redirectPath);
  };

  const handleSubmitNewTicket = () => {
    setShowSuccess(false);
    setCreatedTicketId(null);
    setOversizedFiles([]); // Clear oversized files when starting new ticket
    setTemplateFields([]);
    setCustomFieldValues({});
    // Form is already reset, just need to hide success screen
  };

  // Render custom field based on field type
  const renderCustomField = (field) => {
    const value = customFieldValues[field.fieldCode] || '';
    const errorKey = `custom_${field.fieldCode}`;
    const hasError = errors[errorKey];
    const baseClassName = `w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${hasError ? 'border-red-500' : 'border-gray-300'
      } bg-white text-gray-900`;

    switch (field.fieldType) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            rows={4}
            className={`${baseClassName} resize-none`}
          />
        );

      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            className={baseClassName}
          />
        );

      case 'select':
        try {
          const options = field.fieldOptions ? JSON.parse(field.fieldOptions) : [];
          return (
            <select
              value={value}
              onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
              className={baseClassName}
            >
              <option value="">Select {field.fieldName.toLowerCase()}...</option>
              {Array.isArray(options) && options.map((option, index) => (
                <option key={index} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        } catch (e) {
          console.error('Error parsing select options:', e);
          return (
            <input
              type="text"
              value={value}
              onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
              placeholder="Invalid options configuration"
              className={baseClassName}
              disabled
            />
          );
        }

      case 'checkbox':
        try {
          const options = field.fieldOptions ? JSON.parse(field.fieldOptions) : [];
          return (
            <div className="space-y-2">
              {Array.isArray(options) && options.map((option, index) => {
                const isChecked = Array.isArray(value) ? value.includes(option) : false;
                return (
                  <label key={index} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={(e) => {
                        const currentValue = Array.isArray(value) ? value : [];
                        const newValue = e.target.checked
                          ? [...currentValue, option]
                          : currentValue.filter(v => v !== option);
                        handleCustomFieldChange(field.fieldCode, newValue);
                      }}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">{option}</span>
                  </label>
                );
              })}
            </div>
          );
        } catch (e) {
          console.error('Error parsing checkbox options:', e);
          return (
            <p className="text-sm text-red-600">Invalid checkbox options configuration</p>
          );
        }

      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleCustomFieldChange(field.fieldCode, e.target.value)}
            placeholder={`Enter ${field.fieldName.toLowerCase()}...`}
            className={baseClassName}
          />
        );
    }
  };

  if (loadingReferences) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading ticket form...</p>
        </div>
      </div>
    );
  }

  // Show success screen after successful submission
  if (showSuccess) {
    return (
      <div className="w-full p-6">
        <Card className="text-center">
          <CardContent className="pt-12 pb-12">
            <div className="flex flex-col items-center justify-center space-y-6">
              {/* Success Icon */}
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-12 w-12 text-green-600" />
              </div>

              {/* Success Message */}
              <div className="space-y-2">
                <h2 className="text-3xl font-bold text-gray-900">
                  Ticket Submitted Successfully!
                </h2>
                <p className="text-lg text-gray-600">
                  Your support ticket has been created and submitted successfully.
                </p>
                {createdTicketId && (
                  <p className="text-sm text-gray-500 mt-2">
                    Ticket ID: <span className="font-semibold">#{createdTicketId}</span>
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-center gap-4 mt-8">
                <Button
                  onClick={handleGoToMyTickets}
                  className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3"
                >
                  <Ticket className="h-5 w-5" />
                  <span>Go to My Tickets</span>
                </Button>
                <Button
                  onClick={handleSubmitNewTicket}
                  variant="outline"
                  className="flex items-center space-x-2 border-blue-600 text-blue-600 hover:bg-blue-50 px-6 py-3"
                >
                  <Plus className="h-5 w-5" />
                  <span>Submit New Ticket</span>
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full p-6">
      {/* Header */}
      {/* <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {title}
        </h1>
        <p className="text-gray-600">
          {subtitle}
        </p>
      </div> */}

      {/* Parent-context banner — only renders when launched as "create child" from a
          parent ticket (agent path + feature flag on). Customer portal never sees this. */}
      {isCreateChildMode && (
        <div className="mb-6 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 p-4">
          <div className="flex items-start gap-3">
            <GitBranch className="h-5 w-5 mt-0.5 text-blue-700 dark:text-blue-300 flex-shrink-0" />
            <div className="text-sm">
              <div className="font-medium text-blue-900 dark:text-blue-100">
                Creating a child ticket
              </div>
              <div className="mt-1 text-blue-800 dark:text-blue-200">
                This new ticket will be created and automatically linked under parent{' '}
                <span className="font-mono font-semibold">
                  {rawParentTicketNumber || `#${parentTicketIdNum}`}
                </span>
                . The two tickets remain fully independent — status, assignment, SLA and
                escalation are not propagated.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>Ticket Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Subject */}
            <div ref={(el) => { fieldScrollRefs.current.subject = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Subject <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                placeholder="Enter a brief description of your issue..."
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.subject
                  ? 'border-red-500'
                  : 'border-gray-300'
                  } bg-white text-gray-900`}
              />
              {errors.subject && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.subject}
                </p>
              )}
            </div>

            {/* Category and Phone Number in a single row */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Category */}
              <div ref={(el) => { fieldScrollRefs.current.category = el; }}>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  Category <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                </label>
                <select
                  value={formData.categoryId || ''}
                  onChange={(e) => handleInputChange('categoryId', e.target.value ? Number(e.target.value) : null)}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.category
                    ? 'border-red-500'
                    : 'border-gray-300'
                    } bg-white text-gray-900`}
                >
                  <option value="">Select a category...</option>
                  {categories.length > 0 ? (
                    categories.map((category) => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.categoryName}
                      </option>
                    ))
                  ) : (
                    <option value="" disabled>No categories available</option>
                  )}
                </select>
                {errors.category && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.category}
                  </p>
                )}
              </div>

              {/* Phone Number */}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={formData.phoneNumber}
                  onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                  placeholder="Enter your phone number (optional)..."
                  maxLength={10}
                  inputMode="numeric"
                  pattern="[0-9]*"
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.phoneNumber
                    ? 'border-red-500'
                    : 'border-gray-300'
                    } bg-white text-gray-900`}
                />
                {errors.phoneNumber && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
            </div>

            {/* Location Selector */}
            <div ref={(el) => { fieldScrollRefs.current.regionId = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Location <span className="text-red-500 font-semibold" aria-hidden="true">*</span>
              </label>
              <LocationSection
                key={locationPrefillKey}
                initialValues={locationInitialValues}
                onLocationChange={(location) => {
                  // Ignore empty cascade noise so prefill is not wiped after init.
                  setLocationData((prev) => {
                    if (
                      prev.regionId != null &&
                      location.regionId == null &&
                      location.countyId == null &&
                      location.subCountyId == null
                    ) {
                      return prev;
                    }
                    return {
                      countryId: location.countryId,
                      regionId: location.regionId,
                      countyId: location.countyId,
                      subCountyId: location.subCountyId,
                      wardId: null,
                    };
                  });
                }}
                showErrors={Boolean(errors.regionId || errors.countyId || errors.subCountyId)}
                errors={{
                  regionId: errors.regionId,
                  countyId: errors.countyId,
                  subCountyId: errors.subCountyId,
                }}
                required
                subCountyRequired={false}
                showCountry={false}
                horizontal
              />
            </div>

            {/* Description */}
            <div ref={(el) => { fieldScrollRefs.current.description = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Description <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder="Please provide detailed information about your issue..."
                rows={8}
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.description
                  ? 'border-red-500'
                  : 'border-gray-300'
                  } bg-white text-gray-900 resize-none`}
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.description.length}/5000 characters
              </p>
              {errors.description && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.description}
                </p>
              )}
            </div>

            {/* Dynamic Template Fields - Only show for Custom Fields categories */}
            {formData.categoryId && (() => {
              // Check if selected category uses custom fields (templateType = "2")
              const selectedCategory = categories.find(cat =>
                (cat.categoryId || cat.id) === formData.categoryId
              );
              const isCustomFieldsCategory = selectedCategory &&
                (selectedCategory.templateType === '2' || selectedCategory.template_type === '2');

              // Only show when category uses custom fields and has at least one active template field
              if (!isCustomFieldsCategory || templateFields.length === 0) {
                return null;
              }

              return (
                <div className="border-t pt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Additional Information</h3>
                    {loadingTemplateFields && (
                      <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                    )}
                  </div>

                  <div className="space-y-4">
                    {templateFields.map((field) => (
                      <div
                        key={field.fieldId || field.fieldCode}
                        ref={(el) => {
                          fieldScrollRefs.current[`custom_${field.fieldCode}`] = el;
                        }}
                      >
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          {field.fieldName}{' '}
                          {field.isRequired && (
                            <span className="text-red-500 dark:text-red-500 font-semibold" aria-hidden="true">*</span>
                          )}
                        </label>
                        {renderCustomField(field)}
                        {errors[`custom_${field.fieldCode}`] && (
                          <p className="text-sm text-red-600 mt-1">
                            {errors[`custom_${field.fieldCode}`]}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* File Attachments */}
            <div ref={(el) => { fieldScrollRefs.current.attachments = el; }}>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Attachments
              </label>
              <div className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                errors.attachments
                ? 'border-red-300 bg-red-50'
                : 'border-gray-300 hover:border-blue-400'
                }`}>
                <Upload className={`h-10 w-10 mx-auto mb-3 ${
                  errors.attachments ? 'text-red-400' : 'text-gray-400'
                  }`} />
                <p className={`text-sm mb-3 ${
                  errors.attachments ? 'text-red-700' : 'text-gray-600'
                  }`}>
                  Drag and drop files here, or click to select
                </p>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                  accept="*/*"
                />
                <label
                  htmlFor="file-upload"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  Choose Files
                </label>
                <p className={`text-xs mt-3 ${
                  errors.attachments ? 'text-red-600 font-semibold' : 'text-gray-500'
                  }`}>
                  {errors.attachments
                    ? `⚠️ ${errors.attachments}`
                    : 'All file types supported. Maximum 5MB per file.'}
                </p>
              </div>

              {/* Valid Attachments */}
              {formData.attachments.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-gray-700 mb-2">Valid Files ({formData.attachments.length}):</p>
                  {formData.attachments.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-green-600" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {file.name}
                          </p>
                          <p className="text-xs text-gray-600">
                            {formatFileSize(file.size)}
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
                        aria-label="Remove file"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Oversized Files (Rejected) */}
              {oversizedFiles.length > 0 && (
                <div className="mt-4 space-y-2">
                  <p className="text-xs font-medium text-red-700 mb-2 flex items-center">
                    <AlertCircle className="h-4 w-4 mr-1" />
                    Files Exceeding Size Limit ({oversizedFiles.length}) - Remove these before submitting:
                  </p>
                  {oversizedFiles.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border-2 border-red-300">
                      <div className="flex items-center space-x-3 flex-1">
                        <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-red-900 truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-red-700">
                            {formatFileSize(file.size)} - Exceeds 5MB limit
                          </p>
                          <p className="text-xs text-red-600 mt-1 font-semibold">
                            ⚠️ This file must be removed before you can submit the ticket
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeOversizedFile(index)}
                        className="p-1 text-red-600 hover:bg-red-100 rounded transition-colors flex-shrink-0 ml-2"
                        aria-label="Remove oversized file"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end ">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancel}
                disabled={submitting}
                className="flex items-center space-x-2"
              >
                <X size={18} />
                <span>Cancel</span>
              </Button>
              <Button
                type="submit"
                disabled={submitting || loadingReferences || oversizedFiles.length > 0}
                className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed ml-2"
                title={oversizedFiles.length > 0 ? 'Please remove oversized files before submitting' : ''}
              >
                {submitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <span>Submit Ticket</span>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </form>
    </div>
  );
};

export default CreateTicketForm;
